import { useState } from "react";
import {
  useApiTokens,
  useCreateApiToken,
  useRevokeApiToken,
} from "@/hooks/api/useApiTokens";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useConfirm } from "@/hooks/useConfirm";
import { useToast } from "@/hooks/use-toast";

export default function ApiTokens() {
  const { data: tokens, isLoading } = useApiTokens();
  const createToken = useCreateApiToken();
  const revokeToken = useRevokeApiToken();
  const { toast } = useToast();
  const { confirmProps, confirm } = useConfirm();

  const [newTokenName, setNewTokenName] = useState("");
  const [showNewToken, setShowNewToken] = useState<string | null>(null);

  const handleCreateToken = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTokenName.trim()) return;

    createToken.mutate(
      { name: newTokenName },
      {
        onSuccess: (response) => {
          setShowNewToken(response.data.token);
          setNewTokenName("");
        },
      }
    );
  };

  const handleRevokeToken = (tokenId: string) => {
    confirm({
      title: "Revoke Token",
      description:
        "Are you sure you want to revoke this token? Any plugin using it will lose access.",
      confirmLabel: "Revoke",
      variant: "destructive",
      onConfirm: () => revokeToken.mutate(tokenId),
    });
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied to clipboard" });
    } catch {
      toast({ title: "Failed to copy" });
    }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="text-xl font-medium uppercase tracking-wider mb-8">API Tokens</h1>

      <div className="mb-8">
        <p className="text-sm text-muted-foreground mb-4">
          API tokens allow the plugin to authenticate with your account. The
          easiest way to connect is from the plugin itself (Settings &rarr;
          Connect Account) — it creates a token for you. Use this page to
          review connected devices, revoke access, or create a token manually.
        </p>
      </div>

      {/* New token display */}
      {showNewToken && (
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-8">
          <h3 className="text-sm font-medium uppercase tracking-wider mb-2">Your New API Token</h3>
          <p className="text-sm text-muted-foreground mb-3">
            Copy this token now. You won't be able to see it again!
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-[#1e1e1e] px-3 py-2 rounded-md text-sm font-mono break-all">
              {showNewToken}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(showNewToken)}
            >
              Copy
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="mt-3"
            onClick={() => setShowNewToken(null)}
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Create token form */}
      <form onSubmit={handleCreateToken} className="mb-8">
        <h2 className="text-sm font-medium uppercase tracking-wider mb-4">Create New Token</h2>
        <div className="flex gap-2">
          <Input
            type="text"
            value={newTokenName}
            onChange={(e) => setNewTokenName(e.target.value)}
            placeholder="Token name (e.g., MacBook Pro)"
            required
            className="flex-1"
          />
          <Button type="submit" disabled={createToken.isPending}>
            {createToken.isPending ? "Creating..." : "Create"}
          </Button>
        </div>
        {createToken.isError && (
          <p className="text-sm text-destructive mt-2">
            {[
              createToken.error?.message,
              ...Object.values(createToken.error?.errors ?? {}).flat(),
            ]
              .filter(Boolean)
              .join(" — ")}
          </p>
        )}
      </form>

      {/* Token list */}
      <div>
        <h2 className="text-sm font-medium uppercase tracking-wider mb-4">Active Tokens</h2>

        {isLoading ? (
          <p className="text-muted-foreground">Loading tokens...</p>
        ) : tokens && tokens.length > 0 ? (
          <div className="space-y-3">
            {tokens.map((token) => (
              <div
                key={token.id}
                className="flex items-center justify-between p-4 bg-[#191919] border border-[#616161]/20 rounded-lg"
              >
                <div>
                  <p className="font-medium">{token.name}</p>
                  <p className="text-sm text-muted-foreground font-mono">
                    {token.tokenPrefix}...{" "}
                    <span className="font-sans">
                      {token.lastUsedAt
                        ? `Last used ${new Date(token.lastUsedAt).toLocaleDateString()}`
                        : "Never used"}
                    </span>
                  </p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleRevokeToken(token.id)}
                  disabled={revokeToken.isPending}
                >
                  Revoke
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">
            No active tokens. Create one above to get started.
          </p>
        )}
      </div>

      <ConfirmDialog {...confirmProps} />
    </div>
  );
}
